import { trigger } from '@angular/animations';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import {
  AssetColorLabel,
  AssetDto,
  AssetStatus,
  GalleryColorLabel,
  GalleryDto,
  GalleryFeedbackOptions,
} from '@nusign/picflow-backend-types';
import { filter } from 'rxjs';
import { fadeDownIf } from '../../../animations/fade-down.if.animation';
import { fadeUpIf } from '../../../animations/fade-up.if.animation';
import { AssetContextMenuComponent } from '../../../gallery-shared/components/asset-context-menu/asset-context-menu.component';
import { VideoThumbnailComponent } from '../../../gallery-shared/components/video-thumbnail/video-thumbnail.component';
import { CanDisplayAssetPipe } from '../../../gallery-shared/pipes/can-display-asset.pipe';
import { CanDownloadAssetPipe } from '../../../gallery-shared/pipes/can-download-asset.pipe';
import { AssetDownloadButtonComponent } from '../../../gallery/components/asset-download-button/asset-download-button.component';
import { AssetStatusButtonComponent } from '../../../gallery/components/asset-status-button/asset-status-button.component';
import { ColorLabelContextComponent } from '../../../gallery/components/color-label-context/color-label-context.component';
import { FavoriteButtonComponent } from '../../../gallery/components/favorite-button/favorite-button.component';
import { ShareAssetDialogComponent } from '../../../gallery/dialogs/share-asset-dialog/share-asset-dialog.component';
import { AssetStatusIconPipe } from '../../../gallery/pipes/asset-status-icon.pipe';
import { AssetVersionDimensionsPipe } from '../../../gallery/pipes/asset-version-dimensions.pipe';
import { ColorLabelColorPipe } from '../../../gallery/pipes/color-label-color.pipe';
import { AssetService } from '../../../services/asset.service';
import { ContextMenuService } from '../../../services/context-menu.service';
import { DialogService } from '../../../services/dialog.service';
import { ActivityBadgeComponent } from '../../../shared-extra/components/activity-badge/activity-badge.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SmoothCounterComponent } from '../../../shared/components/smooth-counter/smooth-counter.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { InferredHoverDirective } from '../../../shared/directives/inferred-hover.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';
import { FailedAssetVersionUrlPipe } from '../../../shared/pipes/failed-asset-version-url.pipe';

@Component({
  selector: 'app-list-assets-item',
  templateUrl: './list-assets-item.component.html',
  styleUrls: ['./list-assets-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InferredHoverDirective,
    CanDisplayAssetPipe,
    AssetVersionDimensionsPipe,
    AssetVersionUrlPipe,
    FailedAssetVersionUrlPipe,
    VideoThumbnailComponent,
    SvgComponent,
    ActivityBadgeComponent,
    ContextMenuComponent,
    AssetContextMenuComponent,
    AssetDownloadButtonComponent,
    CanDownloadAssetPipe,
    FavoriteButtonComponent,
    SmoothCounterComponent,
    ColorLabelContextComponent,
    AssetStatusButtonComponent,
    AssetStatusIconPipe,
    ColorLabelColorPipe,
    ButtonDirective,
    TooltipDirective,
    RouterLink,
    AsyncPipe,
    ResizeDirective,
  ],
  animations: [
    trigger('selectedAssetsActions', fadeUpIf),
    trigger('assetActionsTop', fadeDownIf),
    trigger('assetActionsBottom', fadeUpIf),
  ],
})
export class ListAssetsItemComponent {
  private contextMenuService = inject(ContextMenuService);
  private assetService = inject(AssetService);
  private dialog = inject(DialogService);
  private check = inject(ChangeDetectorRef);

  asset = input.required<AssetDto>();
  gallery = input.required<GalleryDto>();
  galleryPermissions = input.required<GalleryDto['permissions']>();
  allowedAndEnabledFeedbackOptions = input.required<GalleryFeedbackOptions[]>();
  colorLabels = input.required<GalleryColorLabel[]>();
  selected = input<boolean>();
  inView = input<boolean>();
  dragging = input<boolean>();
  draggingAny = input<boolean>();
  clientMode = input<boolean>();
  cellSize = input<number>();
  dark = input<boolean>();
  isDesktop = input.required<boolean>();
  isMobile = input.required<boolean>();
  pageTransitions = input<boolean>(false);

  assetClick = output<MouseEvent>();
  assetRightClick = output<MouseEvent>();
  onNewVersion = output<void>();
  toggleAssetLike = output<void>();
  updateAssetColorLabel = output<AssetColorLabel>();
  updateAssetStatus = output<AssetStatus>();

  inferredHovering = signal(false);
  hoverAssetOffsetX = signal(0);

  hovering = signal(false);

  x = this.assetService.updates
    .pipe(
      takeUntilDestroyed(),
      filter(event => event && event.asset.id === this.asset().id && event.action === 'updated')
    )
    .subscribe(() => {
      this.check.detectChanges();
    });

  private getDivFromParentElement(element: HTMLElement): HTMLElement {
    if (element.tagName === 'DIV' && element.classList.contains('asset')) return element as HTMLElement;
    if (!element.parentElement) return;
    return this.getDivFromParentElement(element.parentElement);
  }

  assetMouseMove(event: MouseEvent) {
    const x = event.clientX;
    const box = this.getDivFromParentElement(event.target as HTMLElement)?.getBoundingClientRect();
    if (box) this.hoverAssetOffsetX.set((x - box.x) / box.width);
  }

  stopPropagation(event: Event) {
    this.contextMenuService.closeContext();
    event.stopPropagation();
  }

  openShareDialog() {
    this.dialog.open(ShareAssetDialogComponent, { gallery: this.gallery(), asset: this.asset() });
  }
}
